import {
  CreateCustomFieldInput,
  EditCustomFieldMutationFn,
  RestoreCustomFieldMutationFn,
  SoftRemoveCustomFieldMutationFn,
  useEditCustomFieldMutation,
  useRestoreCustomFieldMutation,
  useSoftRemoveCustomFieldMutation,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission } from 'shared'
import {
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import { createStrictContext } from 'utils/createContext'
import { useUserRoleSelectContext } from 'components/CIMSSelect/common/UserRoleSelect/UserRoleSelectContext'
import { useCallback } from 'react'
import { CustomFieldToClassTypeFormItem } from './components/CustomFieldToClassTypeFormItems/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'

const { Provider, useContextFn } = createStrictContext<{
  edit: {
    fn: EditCustomFieldMutationFn
    is: boolean
    can: boolean
  }
  remove: {
    fn: SoftRemoveCustomFieldMutationFn
    is: boolean
  }
  restore: {
    fn: RestoreCustomFieldMutationFn
    is: boolean
  }
  toInputs: (
    customFieldToClassTypes?: CustomFieldToClassTypeFormItem[]
  ) => CreateCustomFieldInput['customFieldToClassTypeInputs']
}>('CustomFieldPageContext')

export const useCustomFieldPageContext = useContextFn

export const CustomFieldPageProvider: React.FC = ({ children }) => {
  const { refetch } = useProjectCustomFieldsContext()
  const [edit, { loading: editing }] = useEditCustomFieldMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editCustomField: {
        customField: { name },
      },
    }) => simpleEditNotification(name),
  })
  const canEdit = useCheckPermission(Permission.EditCustomField)
  const [remove, { loading: removing }] = useSoftRemoveCustomFieldMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      softRemoveCustomField: {
        customField: { name },
      },
    }) => {
      refetch()
      simpleRemoveNotification(name)
    },
  })
  const [restore, { loading: restoring }] = useRestoreCustomFieldMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      restoreCustomField: {
        customField: { name },
      },
    }) => {
      refetch()
      simpleRestoreNotification(name)
    },
  })

  const { fromLabeledValues } = useUserRoleSelectContext()
  const toInputs = useCallback(
    (customFieldToClassTypes?: CustomFieldToClassTypeFormItem[]) =>
      customFieldToClassTypes?.reduce<
        NonNullable<CreateCustomFieldInput['customFieldToClassTypeInputs']>
      >(
        (
          inputs,
          { editors, alwaysEditable, prominent, required, classTypeId }
        ) => {
          inputs.push({
            classTypeId,
            alwaysEditable: alwaysEditable ?? false,
            required: required ?? false,
            prominent: prominent ?? false,
            ...fromLabeledValues(editors),
          })
          return inputs
        },
        []
      ),
    [fromLabeledValues]
  )
  return (
    <Provider
      value={{
        edit: { fn: edit, is: editing, can: canEdit },
        remove: { fn: remove, is: removing },
        restore: { fn: restore, is: restoring },
        toInputs,
      }}
    >
      {children}
    </Provider>
  )
}
